import { useTranslation } from 'react-i18next';
import { Check, AlertTriangle, RefreshCw, Upload, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ProductStatusPillProps {
  /** `products.approval_status` bzw. die Sitzungsentscheidung. `null` = Altbestand. */
  approvalStatus: string | null;
  /** `products.upload_status`. `null` bei Zeilen, die die Spalte nicht mitbringen. */
  uploadStatus: string | null;
  /**
   * `products.merchant_drift_detected_at`. Nicht-null heisst: der Merchant hat
   * die Quelldaten geaendert, seit die Optimierung lief. Das Original, gegen
   * das die Karte vergleicht, ist dann nicht mehr das aus dem Shop. In der
   * Werkbank war dieser Zustand bis Etappe 3 vollstaendig unsichtbar.
   */
  driftDetectedAt?: string | null;
  /**
   * Im Kartenkopf gesetzt: der Zustand "offen" rendert dann gar nichts. Die
   * Pille saesse dort direkt unter dem aktiven Reiter "Offen" und wiederholte
   * ihn. In der Produktleiste bleibt sie, dort steht kein Reiter daneben.
   */
  hideWhenPending?: boolean;
  /**
   * `pille` (Vorgabe) - Klartext in einem Abzeichen, wie bisher.
   * `marker` - nur ein farbiger Punkt mit dem Klartext im `title` und im
   * zugaenglichen Namen. Gedacht fuer die Produktleiste: dort kostete das
   * Abzeichen bis zu 144 px, und dem Titel blieben 29 von 300.
   *
   * Der Marker zeigt bewusst NUR die ueberraschenden Zustaende. "Offen" sagt
   * bereits der aktive Reiter, ein Punkt dafuer waere Rauschen.
   */
  variante?: 'pille' | 'marker';
}

type PillSpec = {
  key: string;
  /** Tonale Varianten fuer die Signalzustaende — Begruendung in `pillFor`. */
  variant: 'success' | 'scoreWarning' | 'scoreCritical' | 'secondary';
  icon?: typeof Check;
};

/**
 * Der Auslieferungsstand eines Produkts als EINE Pille.
 *
 * Freigabe und Auslieferung sind zwei verschiedene Dinge, und die Werkbank hat
 * sie bisher in einer Spalte vermischt: "Freigegeben" hiess dort sowohl "darf
 * raus" als auch "ist raus". Das ist der Grund, warum dem Feed-Zustand
 * niemand traut.
 *
 * Die REIHENFOLGE ist bindend, erste Uebereinstimmung gewinnt — sie ist nach
 * Dringlichkeit der noetigen Handlung sortiert, nicht nach Datenmodell:
 *
 *   1. `needs_reupload` — bei Google liegt eine Fassung, die es nicht mehr
 *      gibt. Dringendste Aussage, schlaegt auch eine Ablehnung: das alte
 *      Material muss zuerst dort weg.
 *   2. `rejected` — abgelehnt, geht so nicht raus.
 *   3. ungeprueft UND bereits ausgeliefert — der Kern dieses Bereichs. Auf
 *      zwei gemessenen Feeds sind 100 % der "offenen" Produkte laengst live
 *      (googleshopping.xml 3.830 von 3.830, egli-plastic.ch 1.055 von 1.055).
 *      Ohne diese Pille behauptet der Reiter "Offen" Arbeit, die keine ist.
 *   4. ausgeliefert — der einzige Zustand, der Erfolgsfarbe verdient.
 *   5. freigegeben — darf raus, ist aber noch nicht raus. Bewusst NEUTRAL:
 *      Erfolgsfarbe gehoert dem, was wirklich beim Kunden ankommt.
 *   6. sonst offen. Auch fuer den Altbestand mit `approval_status = NULL` —
 *      eine leere Zelle waere keine Aussage, sondern ein Loch.
 */
function pillFor(
  approvalStatus: string | null,
  uploadStatus: string | null,
  driftDetectedAt?: string | null,
): PillSpec {
  // V3-Befund: die deckenden Signal-Varianten fallen mit weisser Schrift
  // durch AA (warning 2.13:1 hell / 1.69:1 dunkel, destructive 3.78 / 2.75 —
  // selbst nachgerechnet). Genau dafuer hat das Repo die TONALEN Varianten
  // (Flaeche + Tinte, 4.67 bis 7.27): dieselbe Loesung, die den Score-Badge
  // gerettet hat. `success` besteht deckend (4.76 / 10.18) und bleibt —
  // Erfolgsfarbe gehoert dem, was wirklich beim Kunden ankommt.
  if (uploadStatus === 'needs_reupload') {
    return { key: 'status.needsReupload', variant: 'scoreWarning', icon: Upload };
  }
  if (approvalStatus === 'rejected') {
    return { key: 'status.rejected', variant: 'scoreCritical', icon: X };
  }
  // Spec 10, bis Etappe 3 in der Werkbank unsichtbar. Steht VOR "live,
  // ungeprueft", weil er die GRUNDLAGE des Vergleichs betrifft und nicht den
  // Versandweg: wer gegen ein veraltetes Original prueft, prueft die falsche
  // Aenderung. Hinter `needs_reupload` bleibt er trotzdem, denn dort liegt bei
  // Google eine Fassung, die es so nicht mehr gibt.
  if (driftDetectedAt) {
    return { key: 'status.drift', variant: 'scoreWarning', icon: RefreshCw };
  }
  if (approvalStatus === 'pending_review' && uploadStatus === 'uploaded') {
    return { key: 'status.liveUnreviewed', variant: 'scoreWarning', icon: AlertTriangle };
  }
  if (uploadStatus === 'uploaded') {
    return { key: 'status.uploaded', variant: 'success', icon: Check };
  }
  if (approvalStatus === 'approved') {
    return { key: 'status.approved', variant: 'secondary', icon: Check };
  }
  return { key: 'status.pending', variant: 'secondary' };
}

export function ProductStatusPill({
  approvalStatus,
  uploadStatus,
  driftDetectedAt,
  hideWhenPending,
  variante = 'pille',
}: ProductStatusPillProps) {
  const { t } = useTranslation('workbench');
  const { key, variant, icon: Icon } = pillFor(approvalStatus, uploadStatus, driftDetectedAt);

  // Das Ausblenden entscheidet der Aufrufer, nicht `pillFor`: die Rangfolge
  // dort ist eine Aussage ueber den Zustand, nicht ueber den Ort, an dem sie
  // steht.
  if (hideWhenPending && key === 'status.pending') return null;

  if (variante === 'marker') {
    // "Offen" und "Freigegeben" sind in der Leiste keine Ueberraschung: den
    // ersten sagt der aktive Reiter, den zweiten die Entscheidung, die man
    // gerade getroffen hat. Ein Punkt dafuer waere Rauschen, und Rauschen ist
    // genau das, was einen Marker unbrauchbar macht.
    if (key === 'status.pending' || key === 'status.approved') return null;

    const ton =
      variant === 'success' ? 'bg-success'
      : variant === 'scoreCritical' ? 'bg-destructive'
      : variant === 'scoreWarning' ? 'bg-warning'
      : 'bg-muted-foreground';

    return (
      <span
        data-status-pill={key}
        data-status-variante="marker"
        title={t(key)}
        className={`inline-block h-[7px] w-[7px] flex-shrink-0 rounded-full ${ton}`}
      >
        {/* Der Klartext geht nicht verloren, er wird nur nicht gezeichnet.
            Ohne ihn waere der Punkt fuer einen Screenreader eine leere
            Flaeche, und die Auskunft haenge allein an der Farbe. */}
        <span className="sr-only">{t(key)}</span>
      </span>
    );
  }

  return (
    <Badge variant={variant} className="gap-1" data-status-pill={key}>
      {Icon && <Icon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />}
      {t(key)}
    </Badge>
  );
}
